import '../css/ReportShowcase.less';

import React, {
  CSSProperties,
  MouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {Link} from 'react-router-dom';
import {Card} from 'semantic-ui-react';

import emptyImg from '../assets/il-no-reports.png';
import emptyImg2x from '../assets/il-no-reports@2x.png';
import ImageIconHeader from '../components/ImageIconHeader';
import EmptyWatermark from './EmptyWatermark';
import {ReportsTableReport} from './ReportsTable';
import {flashFocusCircle} from '../util/flash';
import LegacyWBIcon from './elements/LegacyWBIcon';
import {useUpsertView2Mutation} from '../generated/graphql';
import {reportView} from '../util/urls';
import {useWindowSize} from '../util/window';
import makeComp from '../util/profiler';
import EditableImage from './EditableImage';
import {useApolloClient} from '../state/hooks';
import * as ViewApi from '../state/views/api';

const NARROW_SCREEN_BREAKPOINT = 1200;

interface ReportShowcaseProps {
  reports: ReportsTableReport[];
  readOnly?: boolean;
}

const ReportShowcase: React.FC<ReportShowcaseProps> = makeComp(
  ({reports, readOnly}) => {
    const {width: windowWidth} = useWindowSize();
    const ref = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
      if (ref.current == null || window.location.hash !== '#report-showcase') {
        return;
      }
      ref.current.scrollIntoView({
        block: 'center',
        inline: 'center',
        behavior: 'smooth',
      });
    }, []);

    const [upsertView] = useUpsertView2Mutation();
    const unshowcaseReport = (r: ReportsTableReport) =>
      upsertView({variables: {id: r.id, showcasedAt: new Date(0)}});

    const client = useApolloClient();
    const savePreviewImage = useCallback(
      (r: ReportsTableReport, previewUrl: string) =>
        ViewApi.save(client, {
          id: r.id,
          previewUrl,
        }),
      [client]
    );

    return (
      <>
        <div className="header-wrapper">
          <ImageIconHeader icon="gallery" text="Showcase" />
        </div>
        {reports.length > 0 ? (
          <div ref={ref} id="report-showcase" className="report-showcase">
            <Card.Group
              itemsPerRow={windowWidth <= NARROW_SCREEN_BREAKPOINT ? 1 : 2}>
              {reports.map(r => (
                <Link
                  key={r.id}
                  className="ui card report-showcase-item"
                  to={reportView({
                    entityName: r.project.entityName,
                    projectName: r.project.name,
                    reportID: r.id,
                    reportName: r.displayName,
                  })}>
                  <Card.Content className="report-showcase-item-content">
                    <EditableImage
                      className="report-showcase-item-image"
                      photoUrl={r.previewUrl ?? '/logo.png'}
                      displaySize={120}
                      label={false}
                      readOnly={readOnly}
                      viewID={r.id}
                      updateOverlay="Update Preview"
                      save={url => savePreviewImage(r, url)}
                    />
                    <div className="report-showcase-item-text">
                      <Card.Header className="report-showcase-item-title">
                        <MaxLines lines={2} text={r.displayName} />
                      </Card.Header>
                      {r.description.length > 0 && (
                        <Card.Description className="report-showcase-item-description">
                          <MaxLines lines={2} text={r.description} />
                        </Card.Description>
                      )}
                    </div>
                    {!readOnly && (
                      <LegacyWBIcon
                        name="close"
                        className="report-showcase-item-close"
                        onClick={(e: MouseEvent) => {
                          e.preventDefault();
                          unshowcaseReport(r);
                        }}
                      />
                    )}
                  </Card.Content>
                </Link>
              ))}
            </Card.Group>
          </div>
        ) : (
          <EmptyWatermark
            className="report-showcase-empty-watermark"
            imageSource={emptyImg}
            imageSource2x={emptyImg2x}
            header="Highlight your favorite reports."
            details={
              <>
                Select reports from public projects to feature here.
                <br />
                <span
                  className="add-report-to-showcase underline-dashed"
                  onClick={() => {
                    const el = document.querySelector(
                      '[data-test="showcase-report-button"]'
                    );
                    if (el != null) {
                      flashFocusCircle(el, {
                        minSpaceFromBottom: 200,
                        padding: 0,
                        offsetY: -3,
                        popping: true,
                      });
                    }
                  }}>
                  Add a report
                </span>
              </>
            }
            wide
          />
        )}
      </>
    );
  },
  {id: 'ReportShowcase'}
);

export default ReportShowcase;

interface MaxLinesProps {
  lines: number;
  text: string;
}

const MaxLines: React.FC<MaxLinesProps> = makeComp(
  ({lines, text}) => {
    const {width: windowWidth} = useWindowSize();
    const prevWindowWidthRef = useRef(windowWidth);
    const lineHeightRef = useRef(0);
    const [ready, setReady] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);

    // get line height
    useLayoutEffect(() => {
      const el = ref.current;
      if (el == null) {
        return;
      }
      lineHeightRef.current = el.clientHeight;
      // eslint-disable-next-line
    }, []);

    // update truncated value on prop updates
    useLayoutEffect(() => {
      truncate();
      // eslint-disable-next-line
    }, [lines, text]);

    // update truncated value on viewport resize
    useLayoutEffect(() => {
      const prevWindowWidth = prevWindowWidthRef.current;
      prevWindowWidthRef.current = windowWidth;
      // TODO(axel): more efficient truncation/growing starting from current truncated value
      if (windowWidth < prevWindowWidth) {
        truncate();
      } else if (windowWidth > prevWindowWidth) {
        truncate();
      }
      // eslint-disable-next-line
    }, [windowWidth]);

    const visibilityStyle = useMemo<CSSProperties>(
      () => ({visibility: ready ? 'visible' : 'hidden'}),
      [ready]
    );

    if (text == null) {
      return null;
    }

    return (
      <div ref={ref} style={visibilityStyle}>
        -
      </div>
    );

    function truncate(): void {
      const el = ref.current;
      if (el == null) {
        return;
      }
      let t = text;

      el.textContent = t;
      while (el.clientHeight > lineHeightRef.current * lines) {
        if (t.slice(t.length - 3) === '...') {
          t = t.slice(0, t.length - 4);
        } else {
          t = t.slice(0, t.length - 1);
        }
        t += '...';
        el.textContent = t;
      }

      if (!ready) {
        setReady(true);
      }
    }
  },
  {id: 'MaxLines'}
);
